import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { map, mergeMap, tap, throwError } from 'rxjs';

import { AuthResponse, ImpersonationSession } from '@app/core/models';
import { AdminApiService } from '@app/features/admin/admin-api.service';
import { AdminPartnerApiService } from '@app/features/admin-partners/admin-partner-api.service';
import { AuthStoreService } from './auth-store.service';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class AdminImpersonationService {
  private readonly adminApi = inject(AdminApiService);
  private readonly partnerAdminApi = inject(AdminPartnerApiService);
  private readonly authStore = inject(AuthStoreService);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);

  private readonly sessionState = signal<ImpersonationSession | null>(
    this.storage.getImpersonationSession()
  );

  readonly session = computed(() => this.sessionState());
  readonly isImpersonating = computed(() => !!this.sessionState());

  startClientImpersonation(clientId: string, clientName: string, returnUrl: string) {
    if (this.isImpersonating()) {
      return throwError(() => new Error('Nested impersonation is not allowed.'));
    }

    return this.adminApi.impersonateClient(clientId, returnUrl).pipe(
      mergeMap((response) =>
        this.activateImpersonation(
          response,
          {
            actorType: 'client',
            actorId: clientId,
            actorName: clientName,
            returnUrl,
            startedAtUtc: new Date().toISOString()
          },
          response.redirectTo || '/dashboard'
        )
      )
    );
  }

  startPartnerImpersonation(partnerId: string, partnerName: string, returnUrl: string) {
    if (this.isImpersonating()) {
      return throwError(() => new Error('Nested impersonation is not allowed.'));
    }

    return this.partnerAdminApi.impersonatePartner(partnerId, returnUrl).pipe(
      mergeMap((response) =>
        this.activateImpersonation(
          response,
          {
            actorType: 'partner',
            actorId: partnerId,
            actorName: partnerName,
            returnUrl,
            startedAtUtc: new Date().toISOString()
          },
          response.redirectTo || '/partner/dashboard'
        )
      )
    );
  }

  stopImpersonation() {
    const currentSession = this.session();

    if (!currentSession) {
      return throwError(() => new Error('No impersonation session is active.'));
    }

    return this.adminApi.stopImpersonation(currentSession.returnUrl, this.authStore.refreshToken()).pipe(
      tap((response) => {
        this.storage.clearSelectedClientAccountId();
        this.storage.clearSelectedPropertyId();
        this.authStore.applySession(response);
        this.sessionState.set(null);
        this.storage.clearImpersonationSession();
      }),
      mergeMap((response) =>
        this.authStore.loadAccountState().pipe(
          map(() => {
            void this.router.navigateByUrl(
              response.redirectTo || currentSession.returnUrl || '/admin/dashboard'
            );

            return response;
          })
        )
      )
    );
  }

  clear(): void {
    this.sessionState.set(null);
    this.storage.clearImpersonationSession();
  }

  private activateImpersonation(
    response: AuthResponse,
    session: ImpersonationSession,
    fallbackRoute: string
  ) {
    this.authStore.applySession(response);
    this.sessionState.set(session);
    this.storage.setImpersonationSession(session);

    return this.authStore.loadAccountState().pipe(
      map(() => {
        void this.router.navigateByUrl(response.redirectTo || fallbackRoute);
        return response;
      })
    );
  }
}
